import Controller from '@ember/controller';
import { SERVICE_NAMES } from 'walden-fast/utils/constants/services';
import { service } from '@ember/service';
import type { IntlService } from 'ember-intl';
import type { ShowcaseContentTab } from 'walden-fast/components/showcase-content';
import type { AppServicesWaldenConnectModel } from 'walden-fast/routes/app/services/walden-connect';
import type { Tab } from 'walden-fast/components/tabs';
import type { LinkManagerService } from 'ember-link';
import type { ClientCompany } from 'walden-fast/types/user';

export default class AppServicesWaldenConnectController extends Controller {
  declare model: AppServicesWaldenConnectModel;

  @service('intl') declare intlService: IntlService;
  @service('link-manager') declare linkManager: LinkManagerService;

  serviceName = SERVICE_NAMES.WALDEN_CONNECT;

  hasNonEmptyClientNameLocal = (company: ClientCompany) => {
    const clientNameLocal =
      company.externalIdentifiers?.powerBI?.clientNameLocal;
    return (
      typeof clientNameLocal === 'string' && clientNameLocal.trim().length > 0
    );
  };

  get tabs(): Tab[] {
    const availableClientNameLocals: ClientCompany[] =
      this.model?.availableClientNameLocals ?? [];

    const companiesWithClientNameLocal = availableClientNameLocals.filter(
      (
        company,
      ): company is ClientCompany & {
        externalIdentifiers: { powerBI: { clientNameLocal: string } };
      } => this.hasNonEmptyClientNameLocal(company),
    );

    return companiesWithClientNameLocal.map((clientCompany) => {
      const displayName = clientCompany.displayName;
      const clientNameLocal =
        clientCompany.externalIdentifiers?.powerBI?.clientNameLocal;

      return {
        internalLink: this.linkManager.createLink({
          route: 'app.services.walden-connect.client',
          models: [clientNameLocal], // dynamic segment
        }),
        label: displayName,
        type: 'internal' as const,
      };
    });
  }

  get showcaseTabs(): ShowcaseContentTab[] {
    return [
      {
        label: this.intlService.t(
          'pages.waldenConnect.teaser.tabMonitoring.label',
        ),
        value: 'monitoring',
        iconName: 'eye',
        title: this.intlService.t(
          'pages.waldenConnect.teaser.tabMonitoring.label',
        ),
        description: this.intlService.t(
          'pages.waldenConnect.teaser.tabMonitoring.description',
        ),
        image: '/assets/images/connect_teaser_1.png',
        primaryCta: {
          type: 'activate-service',
          label: this.intlService.t('pages.waldenConnect.teaser.getConnect'),
          value: '',
        },
      },
      {
        label: this.intlService.t(
          'pages.waldenConnect.teaser.tabNotifications.label',
        ),
        value: 'notifications',
        iconName: 'message-circle-warning',
        title: this.intlService.t(
          'pages.waldenConnect.teaser.tabNotifications.label',
        ),
        description: this.intlService.t(
          'pages.waldenConnect.teaser.tabNotifications.description',
        ),
        image: '/assets/images/connect_teaser_2.png',
        primaryCta: {
          type: 'activate-service',
          label: this.intlService.t('pages.waldenConnect.teaser.getConnect'),
          value: '',
        },
      },
      {
        label: this.intlService.t('pages.waldenConnect.teaser.tabData.label'),
        value: 'data',
        iconName: 'database-zap',
        title: this.intlService.t('pages.waldenConnect.teaser.tabData.label'),
        description: this.intlService.t(
          'pages.waldenConnect.teaser.tabData.description',
        ),
        image: '/assets/images/connect_teaser_3.png',
        primaryCta: {
          type: 'activate-service',
          label: this.intlService.t('pages.waldenConnect.teaser.getConnect'),
          value: '',
        },
      },
      {
        label: this.intlService.t(
          'pages.waldenConnect.teaser.tabConfidence.label',
        ),
        value: 'confidence',
        iconName: 'monitor-check',
        title: this.intlService.t(
          'pages.waldenConnect.teaser.tabConfidence.title',
        ),
        description: this.intlService.t(
          'pages.waldenConnect.teaser.tabConfidence.description',
        ),
        image: '/assets/images/connect_teaser_4.png',
        primaryCta: {
          type: 'activate-service',
          label: this.intlService.t('pages.waldenConnect.teaser.getConnect'),
          value: '',
        },
      },
    ];
  }
}
